import { useEffect, useState } from "react";

import * as S from "./styles";

import { Tab } from "../../../TabHeader";
import { StepModalTemplate } from "templates/StepModalTemplate";
import {
  ISubclientRegisterParams,
  ISubclientUpdateParams,
  ISuperficialClient,
} from "interfaces/SubClient";
import { SuperficialClientForm } from "../../../Forms/SuperficialClient/SuperficialClientForm";
import { useSubClientRegister } from "contexts/register/SubClientContext";
import {
  handleSubClientRegisterSubmit,
  handleSubClientUpdateSubmit,
} from "services/handles/SubClientHandles";
import { transformValidatorError } from "utils/transformValidatorError";
import { IExpressValidatorError } from "interfaces/ExpressError";

type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  subClientData?: ISuperficialClient;
  refetch: () => void;
};

const tabs: Tab[] = [{ index: 0, text: "Sub Cliente" }];

export function StepRegisterSubClient({
  opened,
  setOpened,
  subClientData,
  refetch,
}: Props) {
  const {
    setErrors,
    address,
    searching,
    resetSubClient,
    retrieveSubClient,
    subClient,
    establishmentName,
    coordX,
    coordY,
    addError,
    errors,
  } = useSubClientRegister();

  const [tab, setTab] = useState<Tab>(tabs[0]);
  const [deactivatedTabs, setDeactivatedTabs] = useState<Tab[]>([]);
  const [invalidTabs, setInvalidTabs] = useState<Tab[]>([]);

  const [sending, setSending] = useState<boolean>(false);

  useEffect(() => {
    setDeactivatedTabs([]);
    if (!subClientData) {
      setDeactivatedTabs((list) => [...list, tabs[1]]);
      return;
    }
    setDeactivatedTabs([]);
  }, [subClientData]);

  useEffect(() => {
    setInvalidTabs([]);
    if (subClientData) {
      retrieveSubClient(subClientData.id);
      setErrors({});
      return;
    }

    resetSubClient();
  }, [subClientData]); // eslint-disable-line react-hooks/exhaustive-deps

  function setTabAsInvalid(tab: Tab) {
    setInvalidTabs((value) => [...value, tab]);
  }

  function treatValidatorErrors(validatorErrors: IExpressValidatorError) {
    setInvalidTabs([]);
    const { errors, invalidFormTypes } =
    transformValidatorError(validatorErrors);

    let hasAddressError: boolean = false;
    for (const type of invalidFormTypes) {
      if (type.includes("client")) setTabAsInvalid(tabs[0]);
      if (type.includes("user")) setTabAsInvalid(tabs[0]);
      if (type.includes("product")) setTabAsInvalid(tabs[0]);
      if (type.includes("address")) { hasAddressError = true };
    }

    if (hasAddressError) {
      Object.keys(errors).forEach((key) => {
        if (key.includes('address')) {
          delete errors[key];
        }
      })

      errors['addressMap'] = 'O endereço inserido é inválido!'
    }

    setErrors(errors);
  }
  function treatDuplicatedValidator(data: {
    [key: string]: { message: string; value: any };
  }) {
    let response: any = {};
    for (const key in data) {
      if (key.includes("cnpj")) setTabAsInvalid(tabs[0]);
      if (key.includes("cpf")) setTabAsInvalid(tabs[0]);
      if (key.includes("rg")) setTabAsInvalid(tabs[0]);
      response[key] = data[key].message;
    }
    setErrors((err) => {
      return { ...err, ...response };
    });
  }

  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setInvalidTabs([]);
    } else {
      setTabAsInvalid(tabs[0]);
    }
  }, [errors]);

  async function handleSubmit() {
    setSending(true);

    const registerObject: ISubclientRegisterParams = {
      superficialAddress: {
        ...address,
        coordX,
        coordY,
        name: establishmentName!,
      },
      superficialClient: {
        name: subClient.name,
        client: {
          id: subClient.clientId,
        },
      },
    };

    if (subClientData) {
      const updateSubclientObject: ISubclientUpdateParams = {
        superficialAddress: {
          id: subClientData.superficialAddress.id,
          city: address.city ?? subClientData.superficialAddress.city,
          name:
            establishmentName ?? subClientData.superficialAddress.name ?? "",
          district:
            address.district ?? subClientData.superficialAddress.district,
          number: address.number ?? subClientData.superficialAddress.number,
          street: address.street ?? subClientData.superficialAddress.street,
          uf: address.uf ?? subClientData.superficialAddress.uf,
          postalCode:
            address.postalCode ?? subClientData.superficialAddress.postalCode,
          coordX: coordX ?? subClientData.superficialAddress.coordX,
          coordY: coordY ?? subClientData.superficialAddress.coordY,
          cnpj: address.cnpj ?? subClientData.superficialAddress.cnpj,
        },
        superficialClient: {
          id: subClientData.id,
          client: {
            id: subClient.clientId ?? subClientData.client.id,
          },
          name: subClient.name ?? subClientData.name,
        },
      };

      return await handleSubClientUpdateSubmit(updateSubclientObject)
        .then(async (res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do Sub Cliente foram salvos corretamente."
            );
            window.location.reload();
          }
          if (res?.data?.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
          setSending(false);
        })
        .catch((error) => {
          treatValidatorErrors(error.response.data.error);
          setSending(false);
        });
    }
    if (!address.postalCode && !coordX && !coordY) {
      setSending(false);
      return addError(
        "addressMap",
        "O endereço do estabelecimento é obrigatório"
      );
    }
    return await handleSubClientRegisterSubmit(registerObject)
      .then((res) => {
        if (res.status === true) {
          localStorage.setItem(
            "message",
            "Os dados do Sub Cliente foram cadastrados corretamente."
          );
          window.location.reload();
        }
        if (res.status === false) {
          treatValidatorErrors(res.data.error);
        }

        if (res.data.duplicated) {
          treatDuplicatedValidator(res.data.error);
        } else {
          treatValidatorErrors(res.data.error);
        }
      })
      .catch((error) => {
        setSending(false);
      })
      .finally(() => {
        setSending(false);
      });
  }

  function onTabClick(_tab: Tab) {
    setTab(_tab);
  }

  return (
    <StepModalTemplate
      deactivatedTabs={deactivatedTabs}
      opened={opened}
      disableButton={Object.keys(errors).length > 0}
      setOpened={setOpened}
      buttonText={subClientData ? "Salvar edição" : "Efetuar cadastro"}
      handleSubmit={handleSubmit}
      tabs={tabs}
      setSelectedTab={onTabClick}
      selectedTab={tab}
      sending={sending}
      title={`${subClientData ? "Editar" : "Cadastrar"} Sub Cliente`}
      invalidTabs={invalidTabs}
      isOneTabModal
    >
      <S.Container>
        {searching && (
          <S.LoadingWrapper>
            <span>Buscando dados do sub cliente...</span>
          </S.LoadingWrapper>
        )}
        {!searching && (
          <div>
            {tab.index === 0 && (
              <SuperficialClientForm subClientData={subClientData} />
            )}
          </div>
        )}
      </S.Container>
    </StepModalTemplate>
  );
}
